/**
* @license Apache-2.0
*
* Copyright (c) 2025 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import numelDimension from '@stdlib/ndarray/base/numel-dimension';
import getStride from '@stdlib/ndarray/base/stride';
import getOffset from '@stdlib/ndarray/base/offset';
import getData from '@stdlib/ndarray/base/data-buffer';
import { ndarray as strided } from './../../../../base/cumax';


// MAIN //

/**
* Computes the cumulative maximum value of a one-dimensional ndarray.
*
* ## Notes
*
* -   The function expects the following ndarrays:
*
*     -   a one-dimensional input ndarray.
*     -   a one-dimensional output ndarray.
*
* @param {ArrayLikeObject<Object>} arrays - array-like object containing ndarrays
* @returns {ndarrayLike} output ndarray
*
* @example
* import vector from '@stdlib/ndarray/vector/ctor';
*
* var x = vector( [ 1.0, 3.0, 4.0, 2.0 ], 'generic' );
* var y = vector( [ 0.0, 0.0, 0.0, 0.0 ], 'generic' );
*
* var z = cumax( [ x, y ] );
* // returns <ndarray>[ 1.0, 3.0, 4.0, 4.0 ]
*
* var bool = ( z === y );
* // returns true
*/
function cumax( arrays ) {
	var x = arrays[ 0 ];
	var y = arrays[ 1 ];
	strided( numelDimension( x, 0 ), getData( x ), getStride( x, 0 ), getOffset( x ), getData( y ), getStride( y, 0 ), getOffset( y ) ); // eslint-disable-line max-len
	return y;
}


// EXPORTS //

export default cumax;
